import { DefinedUseQueryResult, useQuery, UseQueryOptions } from '@tanstack/react-query';
import axios from 'axios';
import { useCallback } from 'react';
import * as yup from 'yup';
import { Currency } from '~/components/Currency/Currency.hooks';
import { useSearchParams } from '~/components/Route';
export const schema = yup.object().shape({
  eventId: yup.number().optional(),
  group: yup.string().oneOf(['RIVAL_GROUP', 'TOP']).default('RIVAL_GROUP'),
});
export const useChallengeLeaderboardParams = () => {
  const converter = useCallback((params) => schema.castOrDefault(params), []);
  return useSearchParams<{ eventId?: number; group: LeaderboardGroup }>({ converter });
};

export type LeaderboardWeekStatus = 'IN_PROGRESS' | 'END';
export type LeaderboardGroup = 'TOP' | 'RIVAL_GROUP';
export type LeaderboardParams = {
  week?: number;
  group?: LeaderboardGroup;
};
export type Ranking = {
  rank: number;
  profileImageUrl: string;
  nickname: string;
  score: number;
  rewardAmount: number;
  rewardCurrency: Currency;
};

export type Leaderboard = {
  week: number;
  weekStatus: LeaderboardWeekStatus;
  weekStartAt: Date;
  weekEndAt: Date;
  minPurchaseAmountUsd: number;
  rankers: Ranking[];
  expectedPurchaseAmountInUsd?: number;
  expectedRewardAmount?: number;
  expectedRewardCurrency: Currency;
  myProfileImageUrl?: string;
  myRank?: number;
  myScore?: number;
  rivalProfileImageUrl?: string;
  rivalRank?: number;
  rivalScore?: number;
};

export const challengeLeaderboardUrl = '/api/citizen/v1/challenge/ranking';

export const transformChallengeLeaderboard = (data: Leaderboard) => ({
  ...data,
  weekStartAt: new Date(data.weekStartAt),
  weekEndAt: new Date(data.weekEndAt),
});
export const getChallengeLeaderboard = async (params: LeaderboardParams) => {
  const { data } = await axios.get<Leaderboard>(challengeLeaderboardUrl, {
    params,
  });
  return transformChallengeLeaderboard(data);
};
export const useChallengeLeaderboard = (
  params?: LeaderboardParams,
  options?: UseQueryOptions<Leaderboard>
) =>
  useQuery<Leaderboard>({
    queryKey: ['challenge', 'leaderboard', params],
    queryFn: () => getChallengeLeaderboard(params || {}),
    placeholderData: () =>
      ({
        week: 0,
        weekStatus: 'END',
        weekStartAt: new Date(),
        weekEndAt: new Date(),
        minPurchaseAmountUsd: 10,
        rankers: [],
        expectedPurchaseAmountInUsd: 0,
        expectedRewardAmount: 0,
        expectedRewardCurrency: 'KLAY',
        myProfileImageUrl: undefined,
        myRank: 0,
        myScore: 0,
        rivalProfileImageUrl: undefined,
        rivalRank: 0,
        rivalScore: 0,
      }) as Leaderboard,
    keepPreviousData: true,
    ...options,
  }) as DefinedUseQueryResult<Leaderboard>;
